import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import WorkspaceSidebar from '../../components/ui/WorkspaceSidebar';
import UserProfileDropdown from '../../components/ui/UserProfileDropdown';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import LineOfBusinessTabs from './components/LineOfBusinessTabs';
import FilterControls from './components/FilterControls';
import PracticeGrid from './components/PracticeGrid';
import SelectedPracticesPanel from './components/SelectedPracticesPanel';

import Button from '../../components/ui/Button';

const BestPracticePicker = () => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('otc');
  const [selectedPractices, setSelectedPractices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [selectedComplexity, setSelectedComplexity] = useState('');
  const [selectedCompanySize, setSelectedCompanySize] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Mock SAP Best Practices data
  const mockPractices = {
    otc: [
      {
        id: 'otc-1',
        code: '1QM',
        title: 'Quality Management in Sales',
        description: 'Comprehensive quality management processes for sales order processing with inspection planning and quality certificates.',
        version: '2.0',
        complexity: 'medium',
        timeline: '8-12 weeks',
        industry: ['manufacturing', 'automotive', 'pharma'],
        companySize: ['medium', 'large', 'enterprise'],
        benefits: [
          'Automated quality inspection workflows',
          'Certificate of analysis generation',
          'Quality gate enforcement in sales process'
        ],
        prerequisites: [
          'Basic Sales Order Management',
          'Material Master configuration'
        ],
        integrations: ['SD', 'QM', 'MM', 'WM']
      },
      {
        id: 'otc-2',
        code: '1D2',
        title: 'Master Data Governance for Sales',
        description: 'Centralized master data management for customer, material, and pricing information with governance workflows.',
        version: '1.8',
        complexity: 'high',
        timeline: '12-16 weeks',
        industry: ['retail', 'manufacturing', 'chemicals'],
        companySize: ['large', 'enterprise'],
        benefits: [
          'Centralized data governance',
          'Automated approval workflows',
          'Data quality monitoring'
        ],
        prerequisites: [],
        integrations: ['MDG', 'SD', 'MM', 'FI']
      },
      {
        id: 'otc-3',
        code: '2LX',
        title: 'Advanced Available-to-Promise',
        description: 'Real-time availability checking with multi-level ATP and capable-to-match functionality for complex scenarios.',
        version: '2.1',
        complexity: 'expert',
        timeline: '16-20 weeks',
        industry: ['manufacturing', 'automotive'],
        companySize: ['enterprise'],
        benefits: [
          'Real-time availability checking',
          'Multi-level ATP scenarios',
          'Integration with production planning'
        ],
        prerequisites: [
          'Basic Order Management',
          'Production Planning setup'
        ],
        integrations: ['SD', 'PP', 'APO', 'IBP']
      }
    ],
    p2p: [
      {
        id: 'p2p-1',
        code: '5D2',
        title: 'Procurement with Ariba Integration',
        description: 'End-to-end procurement process with Ariba Network integration for supplier collaboration and catalog management.',
        version: '3.0',
        complexity: 'high',
        timeline: '10-14 weeks',
        industry: ['manufacturing', 'retail', 'utilities'],
        companySize: ['medium', 'large', 'enterprise'],
        benefits: [
          'Supplier network integration',
          'Automated catalog management',
          'Collaborative procurement workflows'
        ],
        prerequisites: [
          'Vendor Master setup',
          'Purchase Organization configuration'
        ],
        integrations: ['MM', 'Ariba', 'FI', 'WM']
      },
      {
        id: 'p2p-2',
        code: '5D3',
        title: 'Strategic Sourcing and Contracts',
        description: 'Advanced sourcing processes with contract lifecycle management and spend analysis capabilities.',
        version: '2.5',
        complexity: 'expert',
        timeline: '14-18 weeks',
        industry: ['manufacturing', 'oil-gas', 'utilities'],
        companySize: ['large', 'enterprise'],
        benefits: [
          'Contract lifecycle management',
          'Spend analysis and reporting',
          'Strategic sourcing workflows'
        ],
        prerequisites: [
          'Basic Procurement setup',
          'Vendor evaluation processes'
        ],
        integrations: ['SRM', 'MM', 'FI', 'Analytics']
      }
    ],
    rtr: [
      {
        id: 'rtr-1',
        code: '3F1',
        title: 'Financial Closing Cockpit',
        description: 'Automated financial closing processes with task management, validation rules, and reporting dashboards.',
        version: '2.2',
        complexity: 'medium',
        timeline: '6-10 weeks',
        industry: ['banking', 'manufacturing', 'retail'],
        companySize: ['medium', 'large', 'enterprise'],
        benefits: [
          'Automated closing workflows',
          'Real-time closing status',
          'Compliance reporting'
        ],
        prerequisites: [
          'Basic Financial Accounting',
          'Chart of Accounts setup'
        ],
        integrations: ['FI', 'CO', 'Consolidation', 'BPC']
      },
      {
        id: 'rtr-2',
        code: '3G2',
        title: 'Group Reporting and Consolidation',
        description: 'Multi-entity consolidation with currency translation, elimination entries, and regulatory reporting.',
        version: '1.9',
        complexity: 'expert',
        timeline: '16-24 weeks',
        industry: ['banking', 'manufacturing', 'pharma'],
        companySize: ['enterprise'],
        benefits: [
          'Multi-entity consolidation',
          'Regulatory compliance',
          'Automated eliminations'
        ],
        prerequisites: [
          'Financial Accounting setup',
          'Controlling configuration'
        ],
        integrations: ['BCS', 'FI', 'CO', 'BPC', 'Analytics']
      }
    ],
    plan: [
      {
        id: 'plan-1',
        code: '4P1',
        title: 'Demand-Driven Manufacturing',
        description: 'Advanced production planning with demand sensing, capacity optimization, and supply chain integration.',
        version: '2.3',
        complexity: 'high',
        timeline: '12-18 weeks',
        industry: ['manufacturing', 'automotive', 'chemicals'],
        companySize: ['large', 'enterprise'],
        benefits: [
          'Demand-driven planning',
          'Capacity optimization',
          'Supply chain visibility'
        ],
        prerequisites: [
          'Basic Production Planning',
          'Material Requirements Planning'
        ],
        integrations: ['PP', 'IBP', 'APO', 'MM', 'SD']
      }
    ],
    ewm: [
      {
        id: 'ewm-1',
        code: '6W1',
        title: 'Advanced Warehouse Management',
        description: 'Comprehensive warehouse operations with wave management, labor optimization, and yard management.',
        version: '2.0',
        complexity: 'high',
        timeline: '14-20 weeks',
        industry: ['retail', 'manufacturing', 'automotive'],
        companySize: ['large', 'enterprise'],
        benefits: [
          'Optimized warehouse operations',
          'Labor management',
          'Yard and dock scheduling'
        ],
        prerequisites: [
          'Basic Warehouse Management',
          'Inventory Management setup'
        ],
        integrations: ['EWM', 'WM', 'MM', 'SD', 'TM']
      }
    ]
  };

  // Filter practices based on current filters
  const filteredPractices = useMemo(() => {
    let practices = mockPractices?.[activeTab] || [];

    if (searchTerm) {
      practices = practices?.filter(practice =>
        practice?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        practice?.code?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        practice?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      );
    }

    if (selectedIndustry) {
      practices = practices?.filter(practice =>
        practice?.industry?.includes(selectedIndustry)
      );
    }

    if (selectedComplexity) {
      practices = practices?.filter(practice =>
        practice?.complexity === selectedComplexity
      );
    }

    if (selectedCompanySize) {
      practices = practices?.filter(practice =>
        practice?.companySize?.includes(selectedCompanySize)
      );
    }

    return practices;
  }, [activeTab, searchTerm, selectedIndustry, selectedComplexity, selectedCompanySize]);

  // Calculate practice counts for tabs
  const practiceCount = useMemo(() => {
    return Object.keys(mockPractices)?.reduce((acc, lob) => {
      acc[lob] = mockPractices?.[lob]?.length;
      return acc;
    }, {});
  }, []);

  const handleSelectPractice = (practice) => {
    setSelectedPractices(prev => {
      const isAlreadySelected = prev?.some(p => p?.id === practice?.id);
      if (isAlreadySelected) {
        return prev?.filter(p => p?.id !== practice?.id);
      } else {
        return [...prev, practice];
      }
    });
  };

  const handleRemovePractice = (practiceId) => {
    setSelectedPractices(prev => prev?.filter(p => p?.id !== practiceId));
  };

  const handleClearAllPractices = () => {
    setSelectedPractices([]);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedIndustry('');
    setSelectedComplexity('');
    setSelectedCompanySize('');
  };

  const handleViewDetails = (practice) => {
    console.log('View details for practice:', practice?.code);
    // In a real app, this would open a modal or navigate to a detail page
  };

  const handleProceedToQuestionnaire = () => {
    if (selectedPractices?.length > 0) {
      // Store selected practices in localStorage or state management
      localStorage.setItem('selectedPractices', JSON.stringify(selectedPractices));
      navigate('/questionnaire-runner');
    }
  };

  // Simulate loading when changing tabs
  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => setIsLoading(false), 500);
    return () => clearTimeout(timer);
  }, [activeTab]);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="flex items-center justify-between h-16 px-4">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <svg 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-primary-foreground"
                >
                  <path 
                    d="M3 3h18v18H3V3zm2 2v14h14V5H5zm2 2h10v2H7V7zm0 4h10v2H7v-2zm0 4h6v2H7v-2z" 
                    fill="currentColor"
                  />
                </svg>
              </div>
              <span className="text-xl font-semibold text-foreground font-inter">
                BPMLflows
              </span>
            </div>
          </div>
          <UserProfileDropdown />
        </div>
      </header>
      <div className="flex pt-16">
        {/* Sidebar */}
        <WorkspaceSidebar 
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        />

        {/* Main Content */}
        <main className={`flex-1 nav-transition ${
          isSidebarCollapsed ? 'ml-16' : 'ml-64'
        } md:ml-0 pb-20 md:pb-0`}>
          <div className="max-w-7xl mx-auto p-6 space-y-6">
            {/* Breadcrumb */}
            <BreadcrumbNavigation />

            {/* Page Header */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  SAP Best Practice Picker
                </h1>
                <p className="text-muted-foreground">
                  Select SAP Best Practices to define your implementation scope and proceed to contextual questionnaire.
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => navigate('/app-dashboard')}
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Back to Dashboard
              </Button>
            </div>

            {/* Line of Business Tabs */}
            <LineOfBusinessTabs
              activeTab={activeTab}
              onTabChange={setActiveTab}
              practiceCount={practiceCount}
            />

            {/* Filter Controls */}
            <FilterControls
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              selectedIndustry={selectedIndustry}
              onIndustryChange={setSelectedIndustry}
              selectedComplexity={selectedComplexity}
              onComplexityChange={setSelectedComplexity}
              selectedCompanySize={selectedCompanySize}
              onCompanySizeChange={setSelectedCompanySize}
              onClearFilters={handleClearFilters}
              resultCount={filteredPractices?.length}
            />

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
              {/* Practices Grid */}
              <div className="xl:col-span-3">
                <PracticeGrid
                  practices={filteredPractices}
                  selectedPractices={selectedPractices}
                  onSelectPractice={handleSelectPractice}
                  onViewDetails={handleViewDetails}
                  isLoading={isLoading}
                />
              </div>

              {/* Selected Practices Panel */}
              <div className="xl:col-span-1">
                <div className="sticky top-24">
                  <SelectedPracticesPanel
                    selectedPractices={selectedPractices}
                    onRemovePractice={handleRemovePractice}
                    onClearAll={handleClearAllPractices}
                    onProceedToQuestionnaire={handleProceedToQuestionnaire}
                  />
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default BestPracticePicker;